import React, { useState, useEffect } from 'react'
import Head from 'next/head'
import SignUpSignInForm from '../components/sign-up-sign-in-form'
import { Text, Header2, ReferralWrapper } from '../styles/Components'
import { gsColors } from '../styles/Variables'
import { findByReferralCode } from '../services'
import { useRouter } from 'next/router'
import Image from 'next/image'
import Link from 'next/link'
import { smoothLoad } from '../helpers'

const Referral = ({ history, location }) => {
  const [referredBy, setReferredBy] = useState({})
  const [referralCode, setReferralCode] = useState()

  const router = useRouter()

  useEffect(() => {
    const code = router?.asPath
      ?.split('#')[1] // gets everything after hash
      ?.split('referralCode')[1] // splits at the referralCode param
      ?.slice(1) // removes '=' to get just token string    

    if (!code) {
      router.push('/account/invite-friends')
    }

    setReferralCode(code)
  }, [router, router?.isReady])

  /**
   * Looks up the referrer behind a referral code and stores the result.
   *
   * Swallows the failure: an unrecognized or expired code is an expected outcome, not an error
   * worth surfacing, and referredBy simply stays empty — which suppresses the personalized
   * headline below and leaves the visitor with the plain sign-up form. Deliberately silent;
   * makeSignedAPICall already warns before it rethrows.
   *
   * @param {string} code the referral code from the URL
   */
  const fetch = async code => {
    try {
      const { data } = await findByReferralCode(code)
      setReferredBy(data)
    } catch {
      setReferredBy({})
    }
  }

  // Keyed on the referral code alone. Depending on referredBy here would re-run the effect on
  // every response, because fetch() sets it — and the lookup returns { referrer, rewardAmount },
  // so no guard on the fetched value can reliably stop that. One code, one request.
  useEffect(() => {
    if (!referralCode) {
      return
    }

    fetch(referralCode)
  }, [referralCode])

  return (
    <>
      <Head>
        <meta name="robots" content="noindex, nofollow" />
      </Head>
      <ReferralWrapper>
        <div className="referral-content">
          <div className="referral-copy">
            <div className="logo-container">
              <Link href="/">
                <Image
                  alt="GLAMSQUAD"
                  src={require('../../public/images/new-gs-wordmark-logo.svg')}
                  webp
                  {...smoothLoad}
                />
              </Link>
            </div>

            {/* every sentence here names the referrer, so render nothing until the lookup has
                one. Without the gate an unresolved or unrecognized code leaves the fragment
                " gave you $ to try Glamsquad!" on the page — and now that a failed lookup is
                terminal rather than retried, it would stay there. */}
            {referredBy.referrer && (
              <div>
                <Header2 margin="0 0 15px" color={gsColors.cobalt}>
                  {referredBy.referrer} gave you ${referredBy.rewardAmount} to try
                  Glamsquad!
                </Header2>
                <Text color={gsColors.black}>
                  On Glamsquad you can get hair, makeup, and nail services from
                  talented Beauty Professionals that come to you anytime, anywhere.
                  Sign up to accept {referredBy.referrer}&#39;s invite, and
                  you&#39;ll get ${referredBy.rewardAmount} towards your first
                  appointment.
                </Text>
              </div>
            )}
          </div>

          <div className="form-container">
            <SignUpSignInForm
              history={history}
              location={location}
              onSuccess={() => router.push('/invite-friends')}
              showHeadlines={true}
              referredByCode={referralCode}
            />
          </div>
        </div>
      </ReferralWrapper>
    </>
  )
}

export default Referral
